// What each session in a search's reach was, one row a session, for the
// flow on an empty workspace: who ran it and where, with which agent and
// model, on what work, what it reached for, and where review left it.
//
// The full row needs a read of each session's facts (getSessionFacts),
// which the analytics backend does not serve yet. Without it, the flow is
// drawn from the reads there are: the newest sessions the search matches
// (their machine, agent and model) and the review's findings (where triage
// left each). A session's person is its machine's: the person the directory
// assigns the machine to (getMachinePeople), else the account the machine is
// registered to, else no one known, and the machine stands for its person
// (`personIsMachine`). Its group is the directory person's department
// (`group`, "" without one; `assigned` says whether the directory assigns
// the machine to anyone). Its work is the topic most of its requests were
// (getSessionTopics), arriving after the rest (`topics`: "reading", "done",
// or "failed" when that read could not be made). What it reached for is not
// read, so `partial` says the row stops there.
import { useMemo } from "react";
import { HTTP_METHODS } from "./apiClient.js";
import { useQuery } from "./dataClient.js";
import { dimensionMeta, scopeStepLabel, useLabels } from "./dimensionDefs.js";
import { PLACE_OUTCOME, queuePlace, useFleetFindings } from "./fleetFindings.js";
import { useKeyedReads } from "./keyedReads.js";

const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const NONE = [];

// A machine's name (its hostname, `machineName`, once known), and the account
// it is registered to: without the machine's own name a Windows account
// carries (HOST\name), an email by the part before the @; a generic account
// says no one in particular.
const GENERIC_ACCOUNT = /^(user|users|admin|administrator|root|default|guest|ubuntu|ec2-user)$/i;
export function ownerOf(registeredUser) {
  const account = String(registeredUser || "").trim();
  const name = account.slice(account.lastIndexOf("\\") + 1).split("@")[0].trim();
  return name && !GENERIC_ACCOUNT.test(name) ? name : "";
}
function machineOf(id) {
  if (!id) return { machine: "", machineName: "", owner: "" };
  const label = scopeStepLabel({ dim: "endpoints", id });
  const machineName = label && label !== id ? label : "";
  const machine = machineName || `machine ${id.slice(0, 8)}`;
  return { machine, machineName, owner: ownerOf(dimensionMeta("endpoints", id)?.registeredUser) };
}

// Who ran a session on a machine: the person the directory assigns it to,
// else its owner as recorded, else no one known, and the machine stands for
// its person (said as a machine).
export function personOf({ assigned = "", owner = "", machine = "", onMachine = true }) {
  const person = assigned || owner || machine;
  return { person, personIsMachine: !assigned && !owner && onMachine && !!machine };
}

// `enabled`: false while the page draws its first screen from counts
// (lib/ringCounts.js), reading nothing here.
export function useSessionFacts(scope, range, { enabled = true } = {}) {
  const served = Object.prototype.hasOwnProperty.call(HTTP_METHODS, "getSessionFacts");
  const full = useQuery("getSessionFacts", { scopePath: scope.scopePath, query: scope.steer, range }, { enabled: enabled && served && scope.ready });
  // What there is today: the sessions the search matched (the scope's own
  // match, with its review clauses applied), else the range's newest, and
  // the review.
  const match = useQuery("getSessionMatches", { scopePath: [], range, query: "" }, { enabled: enabled && !served && scope.ready && !scope.q });
  const listed = useMemo(() => (scope.q ? (scope.ready && scope.sessions ? { sessions: scope.sessions, capped: scope.capped } : null) : match.data), [scope.q, scope.ready, scope.sessions, scope.capped, match.data]);
  const failed = scope.q ? scope.match?.error : match.error;
  // Shared with the sidebar's Triage list, which reads the same (cached) findings.
  const fleet = useFleetFindings(range, { enabled });
  const rows = (enabled && listed?.sessions) || NONE;
  const endpoints = useMemo(() => [...new Set(rows.map((s) => s.endpointId).filter(Boolean))], [rows]);
  // Machines' names and owners arrive after the rows; each arrival redraws.
  const labelsTick = useLabels(endpoints.length ? [{ dim: "endpoints", ids: endpoints }] : NONE);
  const ids = useMemo(() => rows.map((s) => s.id), [rows]);
  // Read per session and kept a few minutes, shared with every list that
  // reads the same: a narrowed search reads only the sessions it newly
  // reaches. A machine keeps the person first read for it, so narrowing the
  // search does not move its sessions to another person.
  const topics = useKeyedReads("getSessionTopics", ids, { range }, { enabled: enabled && !served });
  // Whose machine a session ran on is the machine's to say: a session on a
  // machine already read is not asked about again, so a widened search waits
  // only on machines it newly reaches.
  const machines = useMemo(() => new Map(rows.filter((s) => s.endpointId).map((s) => [s.id, s.endpointId])), [rows]);
  const people = useKeyedReads("getMachinePeople", ids, { range }, { enabled: enabled && !served, keep: true, group: machines });
  return useMemo(() => {
    if (!enabled) return { status: "loading", sessions: NONE, partial: true };
    if (served) {
      return { status: full.error ? "error" : full.data ? "done" : "loading", sessions: full.data?.sessions || [], partial: false, topics: "done" };
    }
    if (!listed) return { status: failed ? "error" : "loading", sessions: [], partial: true };
    // Who ran each session is the directory's to say: until it is read, the
    // people would be drawn twice. Where review left each is the findings'
    // (`reviewed`), the range's heaviest read: the sessions are drawn without
    // it until it is in, rather than waiting on it.
    if (ids.length && !people.ready && !people.error) return { status: "loading", sessions: [], partial: true };
    const reviewed = fleet.status === "done";
    const by = new Map();
    for (const f of fleet.findings || []) {
      if (!by.has(f.session)) by.set(f.session, []);
      by.get(f.session).push(f);
    }
    const sessions = rows.map((s) => {
      const found = by.get(s.id) || [];
      const sev = found.reduce((w, f) => ((SEV_RANK[f.severity] ?? 9) < (SEV_RANK[w] ?? 9) ? f.severity : w), null);
      const m = machineOf(s.endpointId);
      const who = people.data?.[s.endpointId];
      return {
        id: s.id, machineId: s.endpointId || null, machine: m.machine, machineName: m.machineName,
        ...personOf({ assigned: who?.name || "", owner: m.owner, machine: m.machine, onMachine: !!s.endpointId }),
        group: who?.department || "", assigned: !!who?.name,
        agent: s.agent || "", model: s.model || "",
        topic: topics.data?.[s.id]?.[0]?.name || "",
        requests: s.requests || 0, startMs: s.firstMs, lastMs: s.lastMs,
        outcome: !reviewed ? undefined : found.length ? PLACE_OUTCOME[queuePlace(found)] : "none", sev, findings: found.length,
      };
    });
    const topicsRead = topics.error ? "failed" : topics.ready || !ids.length ? "done" : "reading";
    return { status: "done", sessions, partial: true, capped: !!listed.capped, reviewed, reviewFailed: fleet.status === "error", topics: topicsRead };
  }, [served, full.data, full.error, listed, failed, rows, ids, fleet.status, fleet.findings, topics.data, topics.ready, topics.error, people.data, people.ready, people.error, labelsTick, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
}
